import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiGetPartners } from 'services/PartnersService'
import { apiFetchStores, apiGetStoreTags } from 'services/StoreService'
import { apiGetAllUsers, apiGetTicketCategories, apiGetTicketStatus } from 'services/TicketsService'
import { apiGetUsers } from 'services/UsersService'
import { orderArrayBy } from 'utils/storeHube/helpers'

export const getTicketStatus = createAsyncThunk('')

const DEFAULT_STORE_PAGE_SIZE = 100

const filterVisibleStores = (stores = []) => stores.filter(store => store.visible === 1)

const buildStoreMeta = (meta = {}, fallbackCount = 0) => {
    const currentPage = meta.current_page ?? meta.page ?? 1
    const perPage = meta.per_page ?? meta['page_size'] ?? DEFAULT_STORE_PAGE_SIZE
    const lastPage = meta.last_page ?? meta.total_pages ?? currentPage
    const total = meta.total ?? meta.record_count ?? fallbackCount

    return {
        page: currentPage,
        perPage,
        lastPage,
        total,
        hasMore: currentPage < lastPage,
        nextPage: currentPage < lastPage ? currentPage + 1 : null,
    }
}

export const getTicketGlobalData = createAsyncThunk('/global/ticket', async () => {
    try {

        const allPromise = await Promise.all([
            apiGetTicketCategories({ query: '/tickets/categories?all=true' }),
            apiGetTicketStatus(),
            apiFetchStores({ page: 1, pageSize: DEFAULT_STORE_PAGE_SIZE, sort: 'id' }),
            apiGetStoreTags(),
            apiGetUsers({ query: '/users?sort=-id' }),
            apiGetPartners({query: '/partners?all=true'})
        ])

        const storesResponse = allPromise[2].data
        const storeData = Array.isArray(storesResponse) ? storesResponse : storesResponse?.data ?? []
        const storeMeta = buildStoreMeta(storesResponse?.meta, storeData.length)

        return {
            ticket_category: allPromise[0].data.data,
            ticket_status: allPromise[1].data.data,
            stores: filterVisibleStores(storeData),
            storeMeta,
            storeTags: allPromise[3].data?.data ?? [],
            users: allPromise[4].data.data, 
            partners: allPromise[5].data.data
        }
    } catch (error) {
        console.log(error);
    }
})

export const fetchTicketStores = createAsyncThunk(
    'ticketList/global/fetchTicketStores',
    async ({ page = 1, tags, tagMode, append = false, search } = {}, { getState }) => {
        try {
            const state = getState()
            const sliceState = state.ticketList?.global ?? {}
            const pageSize = sliceState.storePagination?.perPage ?? DEFAULT_STORE_PAGE_SIZE
            const stateTags = sliceState.storeFilters?.tags ?? []
            const stateSearch = sliceState.storeFilters?.search ?? ''
            // 'any' = tienda con CUALQUIERA de los tags (OR, default historico).
            // 'all' = tienda con TODAS las etiquetas seleccionadas (AND) -> filter[all_tags].
            const stateTagMode = sliceState.storeFilters?.tagMode ?? 'any'

            const resolvedTags = typeof tags !== 'undefined' ? tags : stateTags
            const resolvedSearch = typeof search !== 'undefined' ? search : stateSearch
            const resolvedTagMode = typeof tagMode !== 'undefined' ? tagMode : stateTagMode

            const response = await apiFetchStores({
                page,
                pageSize,
                sort: 'name',
                tags: resolvedTags,
                tagMode: resolvedTagMode,
                search: resolvedSearch,
            })

            const storesResponse = response.data ?? {}
            const storeData = Array.isArray(storesResponse) ? storesResponse : storesResponse.data ?? []

            return {
                stores: filterVisibleStores(storeData),
                meta: buildStoreMeta(storesResponse.meta, storeData.length),
                append,
                tags: resolvedTags,
                tagMode: resolvedTagMode,
                search: resolvedSearch,
            }
        } catch (error) {
            console.log(error)
            throw error
        }
    }
)

export const fetchTicketStoreTags = createAsyncThunk(
    'ticketList/global/fetchTicketStoreTags',
    async () => {
        const response = await apiGetStoreTags()
        return response.data?.data ?? []
    }
)

export const getTicketsCategories = createAsyncThunk('/global/ticketsCategories', async () => {
    const response = await apiGetTicketCategories({ query: '/tickets/categories?all=true' })
    return response.data
})

// Guard de cache: si `allUsers` ya tiene datos, devolvemos null y el reducer
// `getAllUsers.fulfilled` lo ignora. Evita refetchs redundantes en cada
// remount de TicketContent (que ocurre con cada cambio de ticket abierto).
export const getAllUsers = createAsyncThunk(
    '/global/allUsers',
    async (_, { getState }) => {
        const cached = getState().ticketList?.global?.allUsers
        if (Array.isArray(cached) && cached.length > 0) return null
        const response = await apiGetAllUsers()
        return response.data
    }
)




const globalSlice = createSlice({
    name: 'ticketList/global',
    initialState: {
        issetData: false,
        loadingGlobalTicket: true,
        ticketStatus: [],
        ticketCategories: [],
        ticketParentCategories: [],
        allTicketCategories:[], 
        ticketSubCategories: [],
        ticketStores: [],
        storeTags: [],
        storeTagsLoading: false,
        storeFilters: {
            tags: [],
            tagMode: 'any',
            search: '',
        },
        storePagination: {
            page: 1,
            perPage: DEFAULT_STORE_PAGE_SIZE,
            lastPage: 1,
            total: 0,
            hasMore: false,
            nextPage: null,
            loading: false,
        },
        ticketUsers: [],
        storeForTicket: false,
        deviceForTicket: false,
        recordForTicket: false,
        parentIdForTicket: false,
        secondsForTicket: 0,
        addTicketFromCameras: false, 
        allUsers:[], 
        loadingUsers:false, 
        partners: []
    },
    reducers: {
        setTicketStatus: (state, action) => {
            state.ticketStatus = action.payload
        },
        setTicketCategories: (state, action) => {
            state.ticketCategories = action.payload
        },
        setLoadingGlobalTicket: (state, action) => {
            state.loadingGlobalTicket = action.payload
        },
        setStoreForTicket: (state, action) => {
            state.storeForTicket = action.payload
        },
        setDeviceForTicket: (state, action) => {
            state.deviceForTicket = action.payload
        },
        setParentIdForTicket: (state, action) => {
            state.parentIdForTicket = action.payload
        },
        setRecordForTicket: (state, action) => {
            state.recordForTicket = action.payload
        },
        setAddTicketFromCameras: (state, action) => {
            state.addTicketFromCameras = action.payload
        },
        setSecondsForTicket: (state, action) => {
            state.secondsForTicket = action.payload
        },
        resetValuesForTicket: (state, actions) => {
            state.secondsForTicket = 0
            state.recordForTicket = false
            state.deviceForTicket = false
            state.storeForTicket = false
            state.parentIdForTicket = false
        },
        setTicketGlobalData: (state, action) => {

            const { ticket_category, ticket_status, stores = [], users, partners, storeMeta, storeTags } = action.payload

            const storesActive = filterVisibleStores(stores)

            let parentCategories = []
            let subCategories = []

            ticket_category.map(category => {
                const { parent_id } = category
                const categoryParent = { ...category, value: category.id, label: category.label }
                if (!category.parent_id) {
                    const listChild = ticket_category.filter(ca => ca.parent_id === category.id && category.visible)
                    categoryParent.child_categories =  orderArrayBy(listChild, 'label')
                    parentCategories = [...parentCategories, categoryParent]
                } else {
                    subCategories = [...subCategories, categoryParent]
                }
            })

            state.ticketParentCategories = orderArrayBy(parentCategories, 'label')
            state.ticketSubCategories = orderArrayBy(subCategories, 'label')
            state.ticketStatus = ticket_status
            state.ticketStores = storesActive
            if (storeMeta) {
                state.storePagination = { ...state.storePagination, ...storeMeta, loading: false }
            }
            if (storeTags) {
                const toNumber = (val) => {
                    const n = Number(val)
                    return Number.isFinite(n) ? n : Infinity
                }
                state.storeTags = [...storeTags].sort((a, b) => {
                    const pa = toNumber(a?.priority)
                    const pb = toNumber(b?.priority)
                    if (pa !== pb) return pa - pb
                    return String(a?.name ?? '').localeCompare(String(b?.name ?? ''))
                })
            }
            state.ticketUsers = users
            state.partners = partners

            state.loadingGlobalTicket = false
            state.issetData = true
        },
        setStoreFilters: (state, action) => {
            state.storeFilters = {
                ...state.storeFilters,
                ...action.payload,
            }
        },
        clearTicketStores: (state) => {
            state.ticketStores = []
            state.storePagination = {
                ...state.storePagination,
                page: 1,
                lastPage: 1,
                total: 0,
                hasMore: false,
                nextPage: null,
            }
        }
    },
    extraReducers: {
        [getTicketGlobalData.fulfilled]: (state, action) => {
            const { ticket_category, ticket_status, stores = [], users, partners, storeMeta, storeTags } = action.payload

            const storesActive = filterVisibleStores(stores)

            let parentCategories = []
            let subCategories = []

            ticket_category.map(category => {
                const { parent_id } = category
                if (category.visible) {
                    const categoryParent = { ...category, value: category.id, label: category.label }
                    if (!category.parent_id) {
                        const listChild = ticket_category.filter(ca => ca.parent_id === category.id && category.visible)
                        categoryParent.child_categories = orderArrayBy(listChild, 'label')
                        parentCategories = [...parentCategories, categoryParent]
                    } else {
                        subCategories = [...subCategories, categoryParent]
                    }
                }
            })

            state.allTicketCategories=ticket_category
            state.ticketParentCategories = orderArrayBy(parentCategories, 'label')
            state.ticketSubCategories = orderArrayBy(subCategories, 'label')
            state.ticketStatus = ticket_status
            state.ticketStores = storesActive
            state.storePagination = { ...state.storePagination, ...storeMeta, loading: false }
            const toNumber = (val) => {
                const n = Number(val)
                return Number.isFinite(n) ? n : Infinity
            }
            state.storeTags = [...storeTags].sort((a, b) => {
                const pa = toNumber(a?.priority)
                const pb = toNumber(b?.priority)
                if (pa !== pb) return pa - pb
                return String(a?.name ?? '').localeCompare(String(b?.name ?? ''))
            })
            state.ticketUsers = users
            state.partners = partners

            state.loadingGlobalTicket = false
            state.issetData = true
        },
        [getTicketGlobalData.pending]: (state) => {
            state.loadingGlobalTicket = true
            state.storePagination.loading = true
        },

        [getTicketsCategories.fulfilled]: (state, action) => {

            let parentCategories = []
            let subCategories = []

            action.payload.data.map(category => {
                const { parent_id } = category
                if (category.visible) {
                    const categoryParent = { ...category, value: category.id, label: category.label }
                    if (!category.parent_id) {
                        const listChild = action.payload.data.filter(ca => ca.parent_id === category.id && category.visible)
                        categoryParent.child_categories =  orderArrayBy(listChild, 'label')
                        parentCategories = [...parentCategories, categoryParent]
                    } else {
                        subCategories = [...subCategories, categoryParent]
                    }
                }
            })

            state.ticketParentCategories = orderArrayBy(parentCategories, 'label')
            state.ticketSubCategories = subCategories
            state.loadingGlobalTicket = false
            state.issetData = true
        },
        [getTicketsCategories.pending]: (state) => {
            state.loadingGlobalTicket = true
        },

        [getAllUsers.fulfilled]: (state, action) => {
            // payload === null cuando el thunk corto-circuita por cache hit.
            if (action.payload) {
                state.allUsers = action.payload.data
            }
            state.loadingUsers = false
        },

        [getAllUsers.pending]: (state) => {
            state.loadingUsers = true
        },


        [fetchTicketStores.pending]: (state) => {
            state.storePagination.loading = true
        },
        [fetchTicketStores.fulfilled]: (state, action) => {
            const { stores, meta, append, tags, tagMode, search } = action.payload

            if (append) {
                const existingIndexById = new Map(state.ticketStores.map((store, index) => [store.id, index]))
                const merged = [...state.ticketStores]
                stores.forEach(store => {
                    if (existingIndexById.has(store.id)) {
                        merged[existingIndexById.get(store.id)] = store
                    } else {
                        merged.push(store)
                    }
                })
                state.ticketStores = merged
            } else {
                state.ticketStores = stores
            }

            state.storePagination = { ...state.storePagination, ...meta, loading: false }
            state.storeFilters = {
                ...state.storeFilters,
                tags,
                tagMode,
                search,
            }
        },
        [fetchTicketStores.rejected]: (state) => {
            state.storePagination.loading = false
        },

        [fetchTicketStoreTags.pending]: (state) => {
            state.storeTagsLoading = true
        },
        [fetchTicketStoreTags.fulfilled]: (state, action) => {
            state.storeTagsLoading = false
            const list = Array.isArray(action.payload) ? action.payload : []
            const toNumber = (val) => {
                const n = Number(val)
                return Number.isFinite(n) ? n : Infinity
            }
            state.storeTags = [...list].sort((a, b) => {
                const pa = toNumber(a?.priority)
                const pb = toNumber(b?.priority)
                if (pa !== pb) return pa - pb
                return String(a?.name ?? '').localeCompare(String(b?.name ?? ''))
            })
        },
        [fetchTicketStoreTags.rejected]: (state) => {
            state.storeTagsLoading = false
        },


    }
})

export const {

    setTicketStatus,
    setTicketCategories,
    setTicketGlobalData,
    setLoadingGlobalTicket,
    setStoreForTicket,
    setDeviceForTicket,
    setRecordForTicket,
    setAddTicketFromCameras,
    setSecondsForTicket,
    setParentIdForTicket,
    resetValuesForTicket,
    setStoreFilters,
    clearTicketStores

} = globalSlice.actions

export default globalSlice.reducer
